/**
 * Canonical tag definitions for all resource content (blog, research, case studies).
 *
 * Add new tags here — not in individual route files.
 * Articles should use 2-4 tags: typically a condition + topic/format.
 */

/** All allowed tags. Used for validation and documentation. */
export const CANONICAL_TAGS = [
  // Condition tags — what neurological condition the article covers
  'brain injury',
  'stroke',
  'traumatic brain injury',
  'concussion',

  // Topic tags — the subject area
  'neurorehabilitation',
  'cognitive rehabilitation',
  'care',
  'assistive technology',

  // Evidence tags — credibility signals
  'evidence-based',
  'research',

  // Format tags — what kind of content
  'personal story',
  'guest post',
  'company update',
  'case study',
  'tips',

  // Product tags
  'technology',
  'product',
] as const;

export type CanonicalTag = (typeof CANONICAL_TAGS)[number];

/** Colour for each tag, used for chips on listing and detail pages. */
export const TAG_COLORS: Record<CanonicalTag, string> = {
  // Conditions — warm tones
  'brain injury': '#ef4444',
  stroke: '#e11d48',
  'traumatic brain injury': '#dc2626',
  concussion: '#f97316',

  // Topics — cool tones
  neurorehabilitation: '#4d65ff',
  'cognitive rehabilitation': '#3b82f6',
  care: '#ec4899',
  'assistive technology': '#6366f1',

  // Evidence — greens
  'evidence-based': '#10b981',
  research: '#7c3aed',

  // Format — neutrals / accents
  'personal story': '#8b5cf6',
  'guest post': '#f59e0b',
  'company update': '#4d65ff',
  'case study': '#0d9488',
  tips: '#06b6d4',

  // Product — teals
  technology: '#0ea5df',
  product: '#0891b2',
};

/**
 * Get the colour for a tag, with a fallback for any unrecognised tags.
 * This prevents missing-colour issues if an article uses a non-canonical tag.
 */
export function getTagColor(tag: string): string {
  return TAG_COLORS[tag as CanonicalTag] ?? '#6b7280';
}

/** Light tint of a tag colour at a given opacity. */
export function getTagBgColor(tag: string, opacity = 0.12): string {
  const hex = getTagColor(tag);
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

export type TagCategory = 'condition' | 'cognitive' | 'life-area' | 'evidence' | 'product' | 'story' | 'format';

const TAG_CATEGORY_MAP: Partial<Record<CanonicalTag, TagCategory>> = {
  'brain injury': 'condition',
  stroke: 'condition',
  'traumatic brain injury': 'condition',
  concussion: 'condition',

  neurorehabilitation: 'cognitive',
  'cognitive rehabilitation': 'cognitive',
  'assistive technology': 'cognitive',

  care: 'life-area',

  'evidence-based': 'evidence',
  research: 'evidence',

  'personal story': 'story',
  'guest post': 'story',

  'company update': 'format',
  'case study': 'format',
  tips: 'format',

  technology: 'product',
  product: 'product',
};

const CATEGORY_REPRESENTATIVE_TAG: Record<TagCategory, CanonicalTag> = {
  condition: 'brain injury',
  cognitive: 'cognitive rehabilitation',
  'life-area': 'care',
  evidence: 'evidence-based',
  product: 'product',
  story: 'personal story',
  format: 'tips',
};

const FALLBACK_CATEGORY_STYLE = { border: '#e2e8f0', color: '#64748b' };

export function getTagCategory(tag: string): TagCategory | undefined {
  return TAG_CATEGORY_MAP[tag as CanonicalTag];
}

export function getTagCategoryStyle(tag: string): { border: string; color: string } {
  const category = getTagCategory(tag);
  if (!category) return FALLBACK_CATEGORY_STYLE;
  const color = TAG_COLORS[CATEGORY_REPRESENTATIVE_TAG[category]];
  return { border: getTagBgColor(CATEGORY_REPRESENTATIVE_TAG[category], 0.4), color };
}
